import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Save, User, Mail, Phone, Building, MapPin,
  Monitor, Ticket, History, FileText, Edit2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

const DEPARTMENTS = [
  'IT', 'Sales', 'Marketing', 'Finance', 'HR', 'Operations', 
  'Production', 'Warehouse', 'Executive', 'Customer Service', 'Other'
];

const UserDetailPage = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [devices, setDevices] = useState([]);
  const [checkoutHistory, setCheckoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Editable fields
  const [formData, setFormData] = useState({
    department: '',
    job_title: '',
    phone: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    fetchUserData();
  }, [email]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const res = await api.getUserDetail(email);
      const userData = res.data.user || {};
      setUser(userData);
      setTickets(res.data.tickets || []);
      setDevices(res.data.assigned_devices || []);
      setCheckoutHistory(res.data.checkout_history || []);
      
      // Initialize form with existing data
      setFormData({
        department: userData.department || '',
        job_title: userData.job_title || '',
        phone: userData.phone || '',
        location: userData.location || '',
        notes: userData.notes || ''
      });
    } catch (err) {
      console.error('Failed to fetch user:', err);
      toast.error('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateUser(email, formData);
      toast.success('User information saved');
      setEditMode(false);
      // Update local state
      setUser(prev => ({ ...prev, ...formData }));
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in progress': return 'bg-yellow-100 text-yellow-800';
      case 'waiting': return 'bg-purple-100 text-purple-800';
      case 'closed': case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': case 'critical': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading user...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">User not found</p>
          <Button onClick={() => navigate('/users')}>Back to Directory</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/users')} className="gap-1">
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-700 font-semibold text-lg">
                    {user.first_name?.charAt(0) || user.display_name?.charAt(0) || '?'}
                    {user.last_name?.charAt(0) || ''}
                  </span>
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{user.display_name}</h1>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setEditMode(true)}>
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="space-y-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4" />
                  User Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Email</label>
                  <p className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    {user.email}
                  </p>
                </div>
                
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Department</label>
                  {editMode ? (
                    <Select 
                      value={formData.department} 
                      onValueChange={(val) => setFormData(f => ({ ...f, department: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map(dept => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-gray-400" />
                      {user.department || '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Job Title</label>
                  {editMode ? (
                    <Input
                      value={formData.job_title}
                      onChange={(e) => setFormData(f => ({ ...f, job_title: e.target.value }))}
                      placeholder="Enter job title"
                    />
                  ) : (
                    <p className="text-sm">{user.job_title || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Phone</label>
                  {editMode ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <p className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {user.phone || '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Location</label>
                  {editMode ? (
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(f => ({ ...f, location: e.target.value }))}
                      placeholder="Office location"
                    />
                  ) : (
                    <p className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      {user.location || '-'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wide">Source</label>
                  <Badge variant="outline" className="text-xs mt-1">
                    {user.source || 'Unknown'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Add notes about this user..."
                    rows={4}
                  />
                ) : (
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {user.notes || 'No notes'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle Column - Assigned Devices */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  Assigned Devices ({devices.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {devices.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No devices assigned</p>
                ) : (
                  <div className="space-y-3">
                    {devices.map((device, idx) => (
                      <div
                        key={device.asset_id || device.endpoint_id || idx}
                        onClick={() => {
                          const id = device.asset_id || device.endpoint_id;
                          if (id) navigate(`/assets/${id}`);
                        }}
                        className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{device.name || device.endpoint_name || 'Unknown Device'}</p>
                            <p className="text-xs text-gray-500">
                              {device.asset_type || 'Device'} • {device.assigned_to || 'Assigned'}
                            </p>
                          </div>
                          <Badge className={`text-xs ${device.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {device.status || 'Active'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Checkout History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Device History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {checkoutHistory.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No checkout history</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {checkoutHistory.map((entry, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-gray-200 pl-3 py-1">
                        <p className="font-medium">{entry.endpoint_name || entry.endpoint_id}</p>
                        <p className="text-xs text-gray-500">
                          {entry.action === 'checkout' ? 'Checked out' : 'Checked in'} on{' '}
                          {entry.date ? new Date(entry.date).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Tickets */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Ticket className="h-4 w-4" />
                  Tickets ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No tickets</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        onClick={() => navigate(`/tickets/managed/${ticket.ticket_id}`)}
                        className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{ticket.summary}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {ticket.created_on ? new Date(ticket.created_on).toLocaleDateString() : ''}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={`${getStatusColor(ticket.status)} text-xs`}>
                              {ticket.status}
                            </Badge>
                            <span className={`text-xs ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserDetailPage;
